import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import PendingApprovalPage from '@/pages/PendingApprovalPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ProtectedRoute - Role-based access control
 * 
 * Props:
 * - allowedRoles: Array of roles allowed to access this route (e.g., ['job_seeker', 'employer'])
 * - requireApproval: If true, employers must be approved (default: true for employer routes)
 * - adminOnly: Shorthand for allowedRoles=['admin']
 */
function ProtectedRoute({ children, allowedRoles = null, adminOnly = false }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Determine allowed roles
  const effectiveAllowedRoles = adminOnly 
    ? ['admin'] 
    : allowedRoles || ['job_seeker', 'employer', 'admin', 'regional_admin'];

  /**
   * Redirect user to their role-appropriate home page
   */
  const redirectToRoleHome = useCallback((userData) => {
    const userRole = userData.role;
    const employerStatus = userData.employer_status;
    
    if (userRole === 'admin') {
      navigate('/dashboard', { state: { user: userData }, replace: true });
    } else if (userRole === 'regional_admin') {
      navigate('/regional-admin', { state: { user: userData }, replace: true });
    } else if (userRole === 'employer') {
      if (employerStatus === 'approved') {
        navigate('/employer/dashboard', { state: { user: userData }, replace: true });
      }
      // Pending employers handled separately
    } else {
      // job_seeker
      navigate('/dashboard', { state: { user: userData }, replace: true });
    }
  }, [navigate]);

  /**
   * Check if user has access based on role and approval status
   * Returns true if access granted, false if redirected
   */
  const checkRoleAccess = useCallback((userData) => {
    const userRole = userData.role;
    const employerStatus = userData.employer_status;
    
    console.log('[ProtectedRoute] Checking access - role:', userRole, 'status:', employerStatus, 'allowedRoles:', effectiveAllowedRoles, 'path:', location.pathname);
    
    // SECURITY: Pending employers can ONLY see PendingApprovalPage
    if (userRole === 'employer' && employerStatus !== 'approved') {
      console.log('[ProtectedRoute] Employer pending/rejected - showing pending approval page');
      setUser(userData);
      setIsAuthenticated('pending_employer');
      return false;
    }
    
    // Check if user's role is in allowed roles
    if (!effectiveAllowedRoles.includes(userRole)) {
      console.log('[ProtectedRoute] Role not allowed, redirecting to appropriate home');
      toast.error('Access denied - you do not have permission to view this page');
      redirectToRoleHome(userData);
      return false;
    }
    
    return true;
  }, [effectiveAllowedRoles, location.pathname, redirectToRoleHome]);

  useEffect(() => {
    // If user data passed from login/AuthCallback, use it directly
    if (location.state?.user) {
      const passedUser = location.state.user;
      console.log('[ProtectedRoute] User from state:', passedUser.email, 'role:', passedUser.role);
      
      if (!checkRoleAccess(passedUser)) {
        return;
      }
      
      setUser(passedUser);
      setIsAuthenticated(true);
      return;
    }

    // Check auth via /api/auth/me
    const checkAuth = async () => {
      console.log('[ProtectedRoute] Checking auth via /api/auth/me for path:', location.pathname);
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          withCredentials: true
        });
        
        console.log('[ProtectedRoute] Auth response:', response.data?.email, 'role:', response.data?.role, 'employer_status:', response.data?.employer_status);
        
        if (response.data) {
          const userData = response.data;
          
          if (!checkRoleAccess(userData)) {
            return;
          }
          
          setUser(userData);
          setIsAuthenticated(true);
        }
      } catch (error) {
        const status = error.response?.status;
        console.log('[ProtectedRoute] Auth check failed:', status);

        if (status === 401 || status === 403) {
          // Cookie session invalid — try recovering from Supabase session
          console.log('[ProtectedRoute] Trying Supabase session recovery...');
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              const res = await axios.post(
                `${BACKEND_URL}/api/auth/supabase-session`,
                { access_token: session.access_token },
                { withCredentials: true },
              );

              // New user without MongoDB account — redirect to onboarding
              if (res.data.needs_onboarding) {
                console.log('[ProtectedRoute] New user needs onboarding, redirecting...');
                navigate('/auth?mode=google-onboarding', {
                  state: {
                    onboarding: true,
                    email: res.data.email,
                    name: res.data.name,
                    picture: res.data.picture,
                    preset_role: res.data.preset_role || null,
                  },
                  replace: true,
                });
                return;
              }

              const userData = res.data.user;
              console.log('[ProtectedRoute] Supabase recovery succeeded:', userData.email);
              if (!checkRoleAccess(userData)) return;
              setUser(userData);
              setIsAuthenticated(true);
              return;
            }
          } catch (sbErr) {
            console.log('[ProtectedRoute] Supabase recovery failed:', sbErr);
          }

          setIsAuthenticated(false);
          navigate('/auth');
        } else {
          // Network error — retry once
          await new Promise(r => setTimeout(r, 500));
          try {
            const retry = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
            if (retry.data) {
              const userData = retry.data;
              if (!checkRoleAccess(userData)) return;
              setUser(userData);
              setIsAuthenticated(true);
              return;
            }
          } catch {
            setIsAuthenticated(false);
            navigate('/auth');
          }
        }
      }
    };

    checkAuth();
  }, [location.state, location.pathname, navigate, checkRoleAccess]);

  // Loading state
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="auth-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
      </div>
    );
  }

  // Pending employer - show locked screen
  if (isAuthenticated === 'pending_employer') {
    return <PendingApprovalPage user={user} />;
  }

  // Not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Authenticated and authorized - render children
  return children;
}

export default ProtectedRoute;
